import { ApiResponse, PaginatedResponse, User, GiftBox, Order, Product } from './types';

// Use current host when on localhost or LAN IP so it works from http://192.168.x.x:3001 too
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isPrivateIp =
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8080/api/v1';
    }
    if (isPrivateIp) {
      return `http://${hostname}:8080/api/v1`;
    }
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
};

const API_URL = getApiUrl();

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * On 401: try once to refresh the access token (using refreshToken cookie), then retry the request.
   * If the request was already /auth/refresh or we already retried, throw 401 so the app can logout.
   */
  private async request<T>(
    endpoint: string,
    options?: RequestInit,
    skipRefreshRetry = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'X-Skip-Device-Validation': 'true', // Disable device fingerprint validation
          ...options?.headers,
        },
        credentials: 'include', // Important for cookies
      });

      // 401: try refresh token once (except for /auth/refresh and /auth/logout)
      if (response.status === 401 && !skipRefreshRetry && endpoint !== '/auth/refresh' && endpoint !== '/auth/logout') {
        const refreshRes = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Skip-Device-Validation': 'true' },
          credentials: 'include',
        });
        if (refreshRes.ok) {
          // New access token set in cookie; retry original request once
          return this.request<T>(endpoint, options, true);
        }
      }

      if (response.status === 401) {
        const error = new Error('Unauthorized') as any;
        error.status = 401;
        throw error;
      }

      const data = await response.json();

      if (!response.ok) {
        const err = data?.error ?? data;
        const details = err?.details ?? data?.details;
        let message = err?.message || data?.message || 'API request failed';
        if (details && Array.isArray(details) && details.length > 0) {
          const parts = details.map((d: { field?: string; message?: string }) => d.message || '').filter(Boolean);
          if (parts.length > 0) message = parts.join('. ');
        }
        const e = new Error(message) as Error & { details?: unknown };
        e.details = details;
        throw e;
      }

      return data;
    } catch (error: any) {
      if (error.status !== 401) {
        console.error('API Error:', error);
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', ...options });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestInit): Promise<T> {
    // For logout, don't send body if undefined (some endpoints don't accept empty body)
    const requestBody = body !== undefined 
      ? JSON.stringify(body) 
      : (endpoint === '/auth/logout' ? undefined : JSON.stringify({}));
    
    return this.request<T>(endpoint, {
      method: 'POST',
      body: requestBody,
      ...options,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient(API_URL);

// Auth API
export const authApi = {
  register: (data: { email: string; password: string; firstName?: string; lastName?: string }) =>
    api.post<ApiResponse>('/auth/register', data),
  
  login: (data: { email: string; password: string }) =>
    api.post<ApiResponse<{ user: User }>>('/auth/login', data),
  
  logout: () =>
    api.post<ApiResponse>('/auth/logout'),
  
  me: () =>
    api.get<ApiResponse<{ user: User }>>('/auth/me'),
  
  refresh: () =>
    api.post<ApiResponse>('/auth/refresh'),
  
  verifyEmail: (token: string) =>
    api.post<ApiResponse>('/auth/verify-email', { token }),
  
  resendVerification: (email: string) =>
    api.post<ApiResponse>('/auth/resend-verification', { email }),
  
  forgotPassword: (email: string) =>
    api.post<ApiResponse>('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.post<ApiResponse>('/auth/reset-password', { token, password }),
  
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post<ApiResponse>('/auth/change-password', data),
  
  subscribePush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api.post<ApiResponse>('/users/push/subscribe', subscription),
  
  unsubscribePush: (endpoint: string) =>
    api.post<ApiResponse>('/users/push/unsubscribe', { endpoint }),
  
  deleteAccount: () =>
    api.delete<ApiResponse>('/users/account'),
  
  // Admin only
  resetUserPassword: (userId: string) =>
    api.post<ApiResponse<{ temporaryPassword: string }>>(`/users/${userId}/reset-password`),
  
  sendNotification: (data: { userId?: string; title: string; body: string; url?: string; data?: Record<string, string>; sendToAll?: boolean }) =>
    api.post<ApiResponse>('/users/send-notification', data),
  
  getUsersWithPushSubscriptions: () =>
    api.get<ApiResponse<{ 
      users: Array<{ _id: string; email: string; firstName?: string; lastName?: string; role: string; pushSubscriptionsCount: number }>; 
      partners: Array<{ giftBoxId: string; activationLink?: string; pushSubscriptionsCount: number }>;
      guests: Array<{ orderId: string; email: string; orderNumber?: string; pushSubscriptionsCount: number }>;
      totalUsers: number;
      totalPartners: number;
      totalGuests: number;
      totalSubscriptions: number;
    }>>('/users/with-push-subscriptions'),
  getGuestApiKeys: () =>
    api.get<ApiResponse<{
      guests: Array<{
        guestApiKey: string;
        email: string;
        orderId: string;
        orderNumber: string;
        productName: string;
        productSlug: string;
        createdAt: string;
        claimed: boolean;
        claimedBy: { email: string; firstName?: string; lastName?: string } | null;
        devices: Array<{
          fingerprint: string;
          userAgent: string;
          ip: string;
          isApproved: boolean;
          approvedAt?: string;
          lastUsedAt: string;
        }>;
        giftBoxIds: string[];
      }>;
      total: number;
    }>>('/users/guest-api-keys'),
  getPartnerTokens: () =>
    api.get<ApiResponse<{
      partners: Array<{
        partnerToken: string;
        activationLink?: string;
        giftBoxId: string;
        productName: string;
        productSlug: string;
        mode: string;
        createdAt: string;
        owner: { email: string; firstName?: string; lastName?: string } | null;
        order: { orderNumber: string; guestEmail?: string; guestApiKey?: string } | null;
        devices: Array<{
          fingerprint: string;
          userAgent: string;
          ip: string;
          isApproved: boolean;
          approvedAt?: string;
          lastUsedAt: string;
        }>;
      }>;
      total: number;
    }>>('/users/partner-tokens'),
};

// GiftBox API
export const giftBoxApi = {
  getAll: (params?: { page?: number; limit?: number; isActive?: boolean; mode?: string }) =>
    api.get<PaginatedResponse<GiftBox>>(`/giftboxes?${new URLSearchParams(params as any).toString()}`),
  
  getById: (id: string) =>
    api.get<ApiResponse<{ giftBox: GiftBox }>>(`/giftboxes/${id}`),
  
  configure: (id: string, data: { mode: string; sequentialConfig?: any }) =>
    api.post<ApiResponse<{ giftBox: GiftBox }>>(`/giftboxes/${id}/configure`, data),
  
  configureAdvanced: (id: string, data: {
    bookletType: string;
    contentType: string;
    redemptionMode: string;
    discoveryMode: string;
    customConfig?: any;
    progressiveConfig?: any;
  }) =>
    api.post<ApiResponse<{ giftBox: GiftBox }>>(`/giftboxes/${id}/configure-advanced`, data),
  
  activate: (giftBoxId: string) =>
    api.post<ApiResponse<{ giftBox: GiftBox }>>('/giftboxes/activate', { giftBoxId }),
  
  generateLink: (giftBoxId: string) =>
    api.post<ApiResponse<{ activationLink: string; token: string }>>('/giftboxes/generate-link', { giftBoxId }),
  
  shuffleVouchers: (giftBoxId: string) =>
    api.post<ApiResponse<{ giftBox: GiftBox }>>('/giftboxes/shuffle', { giftBoxId }),
  
  unlockNextVoucher: (id: string) =>
    api.post<ApiResponse<{ giftBox: GiftBox }>>(`/giftboxes/${id}/unlock-next`),
  
  // Partner access
  accessViaToken: (token: string) =>
    api.get<ApiResponse<{ giftBox: any; locale?: string }>>(`/giftboxes/partner/${token}`),
  
  redeemVoucher: (token: string, voucherNr: number, redeemedBy?: string) =>
    api.post<ApiResponse>(`/giftboxes/partner/${token}/redeem`, { voucherNr, redeemedBy }),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/giftboxes/${id}`),
};

// Order API
export const orderApi = {
  getAll: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<PaginatedResponse<Order>>(`/orders?${new URLSearchParams(params as any).toString()}`),
  
  getById: (id: string) =>
    api.get<ApiResponse<{ order: Order }>>(`/orders/${id}`),
  
  /** Admin: aggiorna email ordine (guestEmail) */
  updateEmail: (orderId: string, guestEmail: string) =>
    api.patch<ApiResponse<{ order: Order }>>(`/orders/${orderId}`, { guestEmail }),
  
  /** Admin: invia di nuovo l'email di conferma ordine */
  resendConfirmationEmail: (orderId: string) =>
    api.post<ApiResponse<{ message: string }>>(`/orders/${orderId}/resend-confirmation-email`),
  
  claim: (orderNumber: string, email: string) =>
    api.post<ApiResponse<{ order: Order }>>('/orders/claim', { orderNumber, email }),
  
  generateReceipt: (orderId: string) =>
    api.post<ApiResponse<{ receiptUrl: string; fileId: string; orderId: string }>>(`/orders/${orderId}/generate-receipt`),
};

// Product API (Admin)
export const productApi = {
  getAll: (params?: { page?: number; limit?: number; isActive?: boolean; search?: string; category?: string }) =>
    api.get<PaginatedResponse<Product>>(`/products?${new URLSearchParams(params as any).toString()}`),
  
  getById: (id: string) =>
    api.get<ApiResponse<{ product: Product }>>(`/products/${id}`),
  
  create: (data: any) =>
    api.post<ApiResponse<{ product: Product }>>('/products', data),
  
  update: (id: string, data: any) =>
    api.put<ApiResponse<{ product: Product }>>(`/products/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/products/${id}`),
  
  generateAIContent: (data: { field: 'name' | 'subtitle' | 'description' | 'termsTemplate'; model?: string; category?: string; context?: string; productContext?: { name?: string; subtitle?: string; description?: string; marketingLabel?: string; category?: string } }) =>
    api.post<ApiResponse<{ content: string; multilingual?: { it: string; en: string; es: string; fr: string } }>>('/products/generate-ai-content', data),
  
  generateAIVoucherContent: (data: { field: 'title' | 'description' | 'terms'; model?: string; voucherNumber?: number; totalVouchers?: number; existingTitles?: string[]; productContext?: { name?: string; category?: string; description?: string }; context?: string }) =>
    api.post<ApiResponse<{ content: string; multilingual?: { it: string; en: string; es: string; fr: string } }>>('/products/generate-ai-voucher-content', data),
  
  translateText: (data: { text: string; sourceLanguage?: 'it' | 'en' | 'es' | 'fr' }) =>
    api.post<ApiResponse<{ it: string; en: string; es: string; fr: string }>>('/products/translate-text', data),

  /** Cover: solo contesto (productName, productDescription?, voucherTitles?) oppure contesto + prompt (hint stile). */
  generateAIImage: (data: {
    prompt?: string;
    productName?: string;
    productDescription?: string;
    voucherTitles?: string[];
  }) =>
    api.post<ApiResponse<{ imageDataUrl: string }>>('/products/generate-ai-image', data),

  // Async product generation (server-side job, then verify & save)
  startGeneration: (data: { category: string; voucherCount: number; model?: string; userPrompt?: string }) =>
    api.post<ApiResponse<{ jobId: string }>>('/products/generations', data),
  listGenerations: () =>
    api.get<ApiResponse<{ generations: ProductGenerationListItem[] }>>('/products/generations'),
  getGeneration: (id: string) =>
    api.get<ApiResponse<{ generation: ProductGenerationDetail }>>(`/products/generations/${id}`),
  /** Save draft product from proposal review. Accepts selected proposals, optional edits, and voucher statuses. */
  saveGeneration: (
    id: string,
    selection?: {
      selectedNameIndex?: number;
      selectedDescriptionIndex?: number;
      voucherStatuses?: Record<string, 'confermato' | 'scartato' | 'proposto'>;
      editedName?: { it: string; en?: string; es?: string; fr?: string };
      editedDescription?: { it: string; en?: string; es?: string; fr?: string };
      voucherEdits?: Record<string, { title?: { it: string; en?: string; es?: string; fr?: string }; description?: { it: string; en?: string; es?: string; fr?: string } }>;
    }
  ) =>
    api.post<ApiResponse<{ product: Product }>>(`/products/generations/${id}/save`, selection ?? {}),
  /** Salva le modifiche convalidate e gli stati voucher (confermato/scartato) su DB per ripristinarle al rientro. editedName/Description e voucherEdits supportano MultiLang { it, en?, es?, fr? }. */
  saveReviewEdits: (
    id: string,
    payload: {
      editedName?: string | { it: string; en?: string; es?: string; fr?: string };
      editedDescription?: string | { it: string; en?: string; es?: string; fr?: string };
      voucherEdits?: Record<string, { title?: string | { it: string; en?: string; es?: string; fr?: string }; description?: string | { it: string; en?: string; es?: string; fr?: string } }>;
      voucherStatuses?: Record<string, 'confermato' | 'scartato' | 'proposto'>;
    }
  ) =>
    api.patch<ApiResponse<{ generation: ProductGenerationDetail }>>(`/products/generations/${id}/review-edits`, payload),
  generateMoreVouchers: (id: string, count: number) =>
    api.post<ApiResponse<{ generation: ProductGenerationDetail }>>(`/products/generations/${id}/more-vouchers`, { count }),
  generateMoreProposals: (id: string, type: 'name' | 'description', count: number) =>
    api.post<ApiResponse<{ generation: ProductGenerationDetail }>>(`/products/generations/${id}/more-proposals`, { type, count }),
  retryGeneration: (id: string, fromScratch?: boolean) =>
    api.post<ApiResponse<{ jobId: string }>>(`/products/generations/${id}/retry`, fromScratch ? { fromScratch: true } : {}),
  pauseGeneration: (id: string) =>
    api.post<ApiResponse<{ jobId: string }>>(`/products/generations/${id}/pause`),
  resumeGeneration: (id: string) =>
    api.post<ApiResponse<{ jobId: string }>>(`/products/generations/${id}/resume`),
  stopGeneration: (id: string) =>
    api.post<ApiResponse<{ jobId: string }>>(`/products/generations/${id}/stop`),
  deleteGeneration: (id: string) =>
    api.delete<ApiResponse>(`/products/generations/${id}`),
  /** Generate preview cover for confirmation modal. Body = same as save (selection + edits). */
  suggestPromptCover: (id: string, body?: Record<string, unknown>) =>
    api.post<ApiResponse<{ prompt: string }>>(`/products/generations/${id}/suggest-prompt-cover`, body ?? {}),
  suggestPromptVoucher: (id: string, body: { voucherNr: number; [k: string]: unknown }) =>
    api.post<ApiResponse<{ prompt: string }>>(`/products/generations/${id}/suggest-prompt-voucher`, body),
  generatePreviewCover: (id: string, body?: Record<string, unknown>) =>
    api.post<ApiResponse<{ imageDataUrl: string; prompt: string }>>(`/products/generations/${id}/generate-preview-cover`, body ?? {}),
  /** Generate preview voucher image. Body: { voucherNr: number, customPrompt?, ...same as save }. */
  generatePreviewVoucher: (id: string, body: { voucherNr: number; [k: string]: unknown }) =>
    api.post<ApiResponse<{ imageDataUrl: string; prompt: string }>>(`/products/generations/${id}/generate-preview-voucher`, body),
  /** Salva immagini di anteprima (copertina/voucher) nel result per ripristino alla riapertura. */
  savePreviewImages: (id: string, body: { coverImage?: string; voucherImages?: Record<string, string> }) =>
    api.patch<ApiResponse<{ previewImages: { coverImage?: string; voucherImages?: Record<string, string> } }>>(`/products/generations/${id}/preview-images`, body),
  /** Create product from generation. Con coverImage/voucherImages oppure skipImageGeneration: true per usare placeholder. */
  confirmWithImages: (id: string, body: {
    selectedNameIndex?: number;
    selectedDescriptionIndex?: number;
    voucherStatuses?: Record<string, 'confermato' | 'scartato' | 'proposto'>;
    editedName?: unknown;
    editedDescription?: unknown;
    voucherEdits?: Record<string, { title?: unknown; description?: unknown }>;
    coverImage?: string;
    voucherImages?: Record<string, string>;
    /** Se true, crea il prodotto senza generare/caricare immagini (placeholder). */
    skipImageGeneration?: boolean;
  }) =>
    api.post<ApiResponse<{ product: Product }>>(`/products/generations/${id}/confirm-with-images`, body),
  /** Confirm a draft product: optional image generation. Pass { generateImages: false } per confermare senza generare immagini. */
  confirmProduct: (id: string, body?: { generateImages?: boolean }) =>
    api.post<ApiResponse<{ productId: string }>>(`/products/${id}/confirm`, body ?? {}),
};

export interface ProductGenerationListItem {
  _id: string;
  userId: string;
  status: 'pending' | 'running' | 'paused' | 'cancelled' | 'completed' | 'failed';
  step: string;
  progressMessage: string;
  input: { category: string; voucherCount: number; model?: string; userPrompt?: string };
  productId?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
  /** Start of current run; set on "Riparti da zero" so "In corso da" resets */
  runStartedAt?: string;
  /** Set when "more vouchers" is queued; cleared when job finishes (frontend polls until null) */
  pendingMoreVouchersRequestedAt?: string;
}

export interface VoucherProposal {
  nr: number;
  title: string | { it: string; en: string; es: string; fr: string };
  description: string | { it: string; en: string; es: string; fr: string };
  termsAndConditions?: string | { it: string; en: string; es: string; fr: string };
  backgroundImage?: string;
  proposalStatus: 'proposto' | 'confermato' | 'scartato';
}

export interface ProductGenerationDetail extends ProductGenerationListItem {
  result?: Partial<Product> & {
    nameProposals?: Array<{ it: string; en: string; es: string; fr: string }>;
    descriptionProposals?: Array<{ it: string; en: string; es: string; fr: string }>;
    vouchers?: VoucherProposal[];
    /** Immagini di anteprima generate nella modale (copertina + voucher), per ripristino alla riapertura. */
    previewImages?: { coverImage?: string; voucherImages?: Record<string, string> };
    /** Modifiche convalidate dall'utente e stati voucher (persistiti per ripristino al rientro). editedName/Description e voucherEdits possono essere string (legacy) o MultiLang. */
    reviewEdits?: {
      editedName?: string | { it: string; en?: string; es?: string; fr?: string };
      editedDescription?: string | { it: string; en?: string; es?: string; fr?: string };
      voucherEdits?: Record<string, { title?: string | { it: string; en?: string; es?: string; fr?: string }; description?: string | { it: string; en?: string; es?: string; fr?: string } }>;
      voucherStatuses?: Record<string, 'confermato' | 'scartato' | 'proposto'>;
    };
  };
}

// Coupon API (Admin)
export interface CouponItem {
  id: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  validFrom?: string;
  validUntil?: string;
  maxUses?: number;
  usedCount: number;
  isActive: boolean;
  createdAt?: string;
}

export const couponApi = {
  getAll: () =>
    api.get<ApiResponse<{ coupons: CouponItem[] }>>('/coupons'),
  create: (data: { code: string; type: 'percentage' | 'fixed'; value: number; validFrom?: string; validUntil?: string; maxUses?: number }) =>
    api.post<ApiResponse<{ coupon: CouponItem }>>('/coupons', data),
  update: (id: string, data: { isActive?: boolean; maxUses?: number }) =>
    api.patch<ApiResponse<{ coupon: CouponItem }>>(`/coupons/${id}`, data),
};

// User API (Admin)
export const userApi = {
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    role?: string; 
    search?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  }) =>
    api.get<PaginatedResponse<User>>(`/users?${new URLSearchParams(params as any).toString()}`),
  
  getById: (id: string) =>
    api.get<ApiResponse<{ user: User }>>(`/users/${id}`),
  
  updateProfile: (data: { firstName?: string; lastName?: string; dateOfBirth?: string }) =>
    api.put<ApiResponse<{ user: User }>>('/users/profile', data),

  createUser: (data: { email: string; password?: string; firstName?: string; lastName?: string; role: 'client' | 'admin' }) =>
    api.post<ApiResponse<{ user: User; temporaryPassword?: string }>>('/users', data),
};

// Guest API (for guest orders)
export const guestApi = {
  getOrder: (apiKey: string) =>
    api.get<ApiResponse<{
      order: {
        id: string;
        orderNumber: string;
        totalAmount: number;
        currency: string;
        status: string;
        paymentStatus: string;
        createdAt: string;
        guestEmail?: string;
      };
      product: {
        id: string;
        name: string;
        slug: string;
        description: string;
        subtitle?: string | null;
        marketingLabel: string;
        coverImage: string;
        theme: any;
        vouchers: any[];
        totalVouchers: number;
      };
      giftBoxes: Array<{
        id: string;
        name: string;
        status: string;
        isActive: boolean;
        activationLink?: string;
        mode: string;
        vouchers: Array<{
          nr: number;
          title: string;
          description?: string;
          status?: string;
        }>;
        metadata?: any;
      }>;
    }>>(`/orders/guest/access?apiKey=${encodeURIComponent(apiKey)}`, {
      headers: {
        'x-guest-api-key': apiKey,
      },
    }),
  
  generateLink: (apiKey: string, giftBoxId: string) =>
    api.post<ApiResponse<{ activationLink: string; token: string }>>(
      `/orders/guest/generate-link?apiKey=${encodeURIComponent(apiKey)}`,
      { giftBoxId },
      {
        headers: {
          'x-guest-api-key': apiKey,
        },
      }
    ),
  
  configureGiftBox: (apiKey: string, data: { giftBoxId: string; mode: string; sequentialConfig?: any; discoveryMode?: string[] }) =>
    api.post<ApiResponse<{ giftBox: any }>>(
      `/orders/guest/configure?apiKey=${encodeURIComponent(apiKey)}`,
      data,
      {
        headers: {
          'x-guest-api-key': apiKey,
        },
      }
    ),
  
  activateGiftBox: (apiKey: string, giftBoxId: string, beneficiaryFirstName: string, beneficiaryLastName: string) =>
    api.post<ApiResponse<{ giftBox: any }>>(
      `/orders/guest/activate?apiKey=${encodeURIComponent(apiKey)}`,
      { giftBoxId, beneficiaryFirstName, beneficiaryLastName },
      {
        headers: {
          'x-guest-api-key': apiKey,
        },
      }
    ),
  
  sendShareLinkEmail: (apiKey: string, data: { recipientEmail: string; shareLink: string; giftBoxId: string }) =>
    api.post<ApiResponse<{ message: string }>>(
      `/orders/guest/send-share-email?apiKey=${encodeURIComponent(apiKey)}`,
      data,
      {
        headers: {
          'x-guest-api-key': apiKey,
        },
      }
    ),
  
  subscribePush: (apiKey: string, subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    api.post<ApiResponse>('/orders/guest/push/subscribe', subscription, {
      headers: {
        'x-guest-api-key': apiKey,
      },
    }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () =>
    api.get<ApiResponse<{ products: number; orders: number; giftBoxes: number; users: number; totalRevenue: number }>>('/dashboard/stats'),
};

// Category API (Admin)
export const categoryApi = {
  getAll: () =>
    api.get<ApiResponse<{ categories: Array<{
      _id: string;
      slug: string;
      name: string | { it: string; en: string; es: string };
      description?: string | { it: string; en: string; es: string };
      isActive: boolean;
      order: number;
      createdAt: string;
      updatedAt: string;
    }> }>>('/categories'),
  
  getActive: () =>
    api.get<ApiResponse<{ categories: Array<{
      _id: string;
      slug: string;
      name: string | { it: string; en: string; es: string };
      description?: string | { it: string; en: string; es: string };
      wizardPrompt?: string;
      wizardPromptKey?: string;
      isActive: boolean;
      order: number;
      createdAt: string;
      updatedAt: string;
    }> }>>('/categories/active'),

  getPromptOptions: () =>
    api.get<ApiResponse<{ promptOptions: Array<{ id: string; label: string; prompt: string }> }>>('/categories/prompt-options'),
  
  getById: (id: string) =>
    api.get<ApiResponse<{ category: {
      _id: string;
      slug: string;
      name: string | { it: string; en: string; es: string };
      description?: string | { it: string; en: string; es: string };
      isActive: boolean;
      order: number;
      createdAt: string;
      updatedAt: string;
    } }>>(`/categories/${id}`),
  
  create: (data: {
    slug?: string;
    name: string | { it: string; en: string; es: string };
    description?: string | { it: string; en: string; es: string };
    wizardPrompt?: string;
    wizardPromptKey?: string;
    isActive?: boolean;
    order?: number;
  }) =>
    api.post<ApiResponse<{ category: any }>>('/categories', data),
  
  update: (id: string, data: {
    slug?: string;
    name?: string | { it: string; en: string; es: string };
    description?: string | { it: string; en: string; es: string };
    wizardPrompt?: string;
    wizardPromptKey?: string;
    isActive?: boolean;
    order?: number;
  }) =>
    api.put<ApiResponse<{ category: any }>>(`/categories/${id}`, data),
  
  delete: (id: string) =>
    api.delete<ApiResponse>(`/categories/${id}`),
};

// Prompts API (Admin) – prompt per product type + version (wizard IA)
export const promptApi = {
  getAll: () =>
    api.get<ApiResponse<{ prompts: Array<{
      _id: string;
      code: string;
      productType: string;
      version: number;
      text: string;
      label?: string;
      createdAt: string;
      updatedAt: string;
    }> }>>('/prompts'),

  getById: (id: string) =>
    api.get<ApiResponse<{ prompt: {
      _id: string;
      code: string;
      productType: string;
      version: number;
      text: string;
      label?: string;
      createdAt: string;
      updatedAt: string;
    } }>>(`/prompts/${id}`),

  update: (id: string, data: { text: string; label?: string }) =>
    api.put<ApiResponse<{ prompt: any }>>(`/prompts/${id}`, data),
};
